import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, Settings, ChevronLeft, ChevronRight, Clock, Video, Phone, Loader2, CheckCircle2, AlertCircle, RefreshCw, Zap, X, Plus, Trash2, Smartphone, Mail, Cloud, MapPin, Users, Link2, FileText, Bot, Bell, PhoneCall } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
type ConnectionState = 'disconnected' | 'connecting' | 'success' | 'error' | 'connected';
type CalendarView = 'month' | 'day';
const EVENTS = [{
  id: 1,
  title: 'Product Sync',
  time: '10:00 AM – 11:00 AM',
  date: 'Tuesday, February 24',
  duration: '1 hour',
  type: 'video' as const,
  location: 'Google Meet',
  meetingLink: 'meet.google.com/abc-defg-hij',
  organizer: 'Sarah Miller',
  attendees: ['Alex Founder', 'Sarah Miller', 'Mike Ross'],
  description: 'Weekly product alignment to review the roadmap, discuss blockers, and prioritize the upcoming sprint.',
  aiManaged: false,
  accent: 'bg-blue-500',
  accentText: 'text-blue-500'
}, {
  id: 2,
  title: 'Client Discovery Call',
  time: '2:00 PM – 2:45 PM',
  date: 'Tuesday, February 24',
  duration: '45 minutes',
  type: 'phone' as const,
  location: '+1 (555) 012-3456',
  meetingLink: '',
  organizer: 'Vexu AI Agent',
  attendees: ['Alex Founder', 'Jordan Lee (Client)'],
  description: 'Initial discovery call booked automatically by your AI agent. Understand the client needs and qualify the opportunity.',
  aiManaged: true,
  originatingCall: {
    id: 2,
    label: 'Mike Ross · 11:15 AM'
  },
  accent: 'bg-vexu',
  accentText: 'text-vexu'
}, {
  id: 3,
  title: 'Design Review',
  time: '3:00 PM – 4:00 PM',
  date: 'Tuesday, February 24',
  duration: '1 hour',
  type: 'video' as const,
  location: 'Zoom',
  meetingLink: 'zoom.us/j/9876543210',
  organizer: 'Emily Chen',
  attendees: ['Design Team', 'Alex Founder', 'Emily Chen'],
  description: 'Review the latest mockups for the onboarding flow and finalize the visual direction before development.',
  aiManaged: false,
  accent: 'bg-purple-500',
  accentText: 'text-purple-500'
}, {
  id: 4,
  title: 'Investor Update',
  time: '5:00 PM – 5:30 PM',
  date: 'Tuesday, February 24',
  duration: '30 minutes',
  type: 'video' as const,
  location: 'Google Meet',
  meetingLink: 'meet.google.com/xyz-uvwx-rst',
  organizer: 'Vexu AI Agent',
  attendees: ['Alex Founder', 'Board Members'],
  description: 'Monthly investor sync covering key metrics, growth highlights, and the next funding milestone.',
  aiManaged: true,
  originatingCall: {
    id: 4,
    label: 'Design Team · Monday'
  },
  accent: 'bg-vexu',
  accentText: 'text-vexu'
}];
type CalendarEvent = (typeof EVENTS)[number];
interface CalendarScreenProps {
  onViewCall?: (callId: number) => void;
  initialEventId?: number | null;
  onInitialEventOpened?: () => void;
}
const EVENT_DAYS = [3, 7, 12, 14, 18, 24, 26];
const fadeSlide = {
  initial: {
    opacity: 0,
    y: 16
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -16
  },
  transition: {
    duration: 0.3,
    ease: [0.4, 0, 0.2, 1]
  }
};
const TODAY_DAY = 24;
type CalendarProvider = 'google' | 'outlook' | 'icloud' | 'phone' | 'yahoo';
interface ConnectedAccount {
  id: string;
  provider: CalendarProvider;
  label: string;
  detail: string;
}
const ALL_PROVIDERS: {
  id: CalendarProvider;
  label: string;
  detail: string;
}[] = [{
  id: 'google',
  label: 'Google Calendar',
  detail: 'Connect a Google account'
}, {
  id: 'outlook',
  label: 'Outlook / Microsoft 365',
  detail: 'Office and personal accounts'
}, {
  id: 'icloud',
  label: 'iCloud Calendar',
  detail: 'Apple ID calendar sync'
}, {
  id: 'phone',
  label: 'Phone Calendar',
  detail: 'Local device calendar'
}, {
  id: 'yahoo',
  label: 'Yahoo Calendar',
  detail: 'Connect a Yahoo account'
}];
const PROVIDER_EMAILS: Record<CalendarProvider, string> = {
  google: 'alex.founder@gmail.com',
  outlook: 'alex@outlook.com',
  icloud: 'alex@icloud.com',
  phone: 'iPhone Local',
  yahoo: 'alex@yahoo.com'
};
export function CalendarScreen({
  onViewCall,
  initialEventId,
  onInitialEventOpened
}: CalendarScreenProps) {
  const {
    isLight
  } = useTheme();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [provider, setProvider] = useState<'google' | 'outlook' | null>(null);
  const [view, setView] = useState<CalendarView>('day');
  const [hasCalendarData, setHasCalendarData] = useState(false);
  const [selectedDate, setSelectedDate] = useState<number>(TODAY_DAY);
  const [showSettings, setShowSettings] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [addingProvider, setAddingProvider] = useState<CalendarProvider | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [reminderIds, setReminderIds] = useState<number[]>([]);
  React.useEffect(() => {
    if (initialEventId === null || initialEventId === undefined) return;
    const event = EVENTS.find(candidate => candidate.id === initialEventId);
    if (event) {
      setHasCalendarData(true);
      setSelectedEvent(event);
    }
    onInitialEventOpened?.();
  }, [initialEventId, onInitialEventOpened]);
  const [reminderToast, setReminderToast] = useState<string | null>(null);
  const toggleReminder = (event: CalendarEvent) => {
    setReminderIds(prev => {
      const isSet = prev.includes(event.id);
      if (isSet) {
        setReminderToast(`Reminder removed for "${event.title}"`);
        return prev.filter(id => id !== event.id);
      }
      setReminderToast(`We'll remind you 15 min before "${event.title}"`);
      return [...prev, event.id];
    });
    setTimeout(() => setReminderToast(null), 2800);
  };
  const handleAddAccount = (p: CalendarProvider) => {
    setAddingProvider(p);
    setTimeout(() => {
      setConnectedAccounts(prev => [...prev, {
        id: `${p}-${Date.now()}`,
        provider: p,
        label: ALL_PROVIDERS.find(x => x.id === p)!.label,
        detail: PROVIDER_EMAILS[p]
      }]);
      setAddingProvider(null);
    }, 1400);
  };
  const handleRemoveAccount = (id: string) => {
    setConnectedAccounts(prev => {
      const next = prev.filter(a => a.id !== id);
      if (next.length === 0) {
        setConnectionState('disconnected');
        setShowSettings(false);
      }
      return next;
    });
  };
  const handleConnect = (p: 'google' | 'outlook') => {
    setProvider(p);
    setConnectionState('connecting');
    setTimeout(() => {
      setConnectionState('success');
      setConnectedAccounts(prev => prev.some(a => a.provider === p) ? prev : [...prev, {
        id: `${p}-${Date.now()}`,
        provider: p,
        label: ALL_PROVIDERS.find(x => x.id === p)!.label,
        detail: PROVIDER_EMAILS[p]
      }]);
      setTimeout(() => setConnectionState('connected'), 1400);
    }, 2200);
  };
  const handleRetry = () => {
    if (provider) handleConnect(provider);
  };
  // ─── Disconnected ────────────────────────────────────────
  const renderDisconnected = () => <motion.div key="disconnected" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-961" data-mp="oi7vn431k46n">
      <div className="relative mb-10" data-id="element-962" data-mp="ybf818w78kg8">
        <div className={`absolute inset-0 rounded-full blur-3xl scale-150 animate-pulse pointer-events-none ${isLight ? 'bg-vexu/20' : 'bg-vexu/15'}`} data-id="element-963" data-mp="ycgjheh4n8yh" />
        <div className={`relative w-24 h-24 rounded-[28px] border flex items-center justify-center transition-colors duration-500 ${isLight ? 'bg-white border-slate-200 shadow-xl shadow-vexu/10' : 'bg-dark-card border-white/10 shadow-2xl shadow-black/60'}`} data-id="element-964" data-mp="5si2smiu9shd">
          <CalendarDays className={`w-11 h-11 ${isLight ? 'text-vexu-600' : 'text-white'}`} strokeWidth={1.6} data-id="element-965" data-mp="34dvy4adyim85" />
        </div>
      </div>

      <h2 className={`text-[22px] font-bold mb-2 tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-966" data-mp="3eaquonqo6cjs">
        Connect Your Calendar
      </h2>
      <p className={`text-sm leading-relaxed mb-10 max-w-[260px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-967" data-mp="399tf8ix1nwlz">
        Let Vexu.ai manage your schedule, book meetings from calls, and keep you
        organized.
      </p>

      <div className="w-full space-y-3 max-w-xs" data-id="element-968" data-mp="vkufx0rka4d">
        <button onClick={() => handleConnect('google')} className={`w-full h-[52px] rounded-2xl backdrop-blur-lg border flex items-center justify-center gap-3 font-medium active:scale-[0.98] transition-all ${isLight ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.08] text-white hover:bg-white/[0.08]'}`} data-id="element-969" data-mp="wdqa2y6pf7q">
          <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-970" data-mp="6hv3kl372i0e">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-971" data-mp="aiyj16hbyc13" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-972" data-mp="1jbg2g8ycy5x" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-973" data-mp="3nzo0bq0putjc" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-974" data-mp="3ewmw5uvevcib" />
          </svg>
          Continue with Google
        </button>

        <button onClick={() => handleConnect('outlook')} className={`w-full h-[52px] rounded-2xl backdrop-blur-lg border flex items-center justify-center gap-3 font-medium active:scale-[0.98] transition-all ${isLight ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.08] text-white hover:bg-white/[0.08]'}`} data-id="element-975" data-mp="v30sc1thiksw">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" data-id="element-976" data-mp="1e1vbro3w89fe">
            <path d="M11.5 24L0 20.5V3.5L11.5 0V24Z" fill="#0078D4" data-id="element-977" data-mp="1dzitf9u83x1t" />
            <path d="M24 5.5V18.5C24 19.33 23.33 20 22.5 20H13V4H22.5C23.33 4 24 4.67 24 5.5Z" fill="#0078D4" opacity="0.7" data-id="element-978" data-mp="1iyvhtz0qtc6i" />
            <ellipse cx="6" cy="12" rx="3.5" ry="4.5" fill="white" data-id="element-979" data-mp="2bhsmaj6a404b" />
          </svg>
          Continue with Outlook
        </button>
      </div>
    </motion.div>;
  // ─── Connecting ──────────────────────────────────────────
  const renderConnecting = () => <motion.div key="connecting" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-980" data-mp="2ulck2d76vql1">
      <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-981" data-mp="2pmszoh88umus">
        <div className="relative w-32 h-32 mb-8" data-id="element-982" data-mp="21x71nx3lydod">
          <motion.div animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.1, 0.3]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-983" data-mp="1xvd2jt929hqb" />
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-984" data-mp="3n86sucun91io" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-985" data-mp="376jy41dia7w">
            <motion.div animate={{
            scale: [1, 1.1, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} data-id="element-986" data-mp="353w9qnp0t6c">
              <CalendarDays className="w-10 h-10 text-vexu" data-id="element-987" data-mp="3bm70c0r93ufs" />
            </motion.div>
          </div>
        </div>
        <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-988" data-mp="3gljqaudf5hml">
          Syncing Calendar
        </p>
        <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-989" data-mp="3lecunh4z6ny5">
          Securely importing your events...
        </p>
      </div>
    </motion.div>;
  // ─── Success ─────────────────────────────────────────────
  const renderSuccess = () => <motion.div key="success" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-990" data-mp="2lan57qg82efu">
      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20
    }} className={`w-20 h-20 rounded-full border flex items-center justify-center mb-6 transition-colors ${isLight ? 'bg-vexu/10 border-vexu/30 shadow-[0_0_30px_rgba(0,200,83,0.2)]' : 'bg-vexu/15 border-vexu/25 shadow-[0_0_40px_rgba(0,200,83,0.15)]'}`} data-id="element-991" data-mp="2bdqude2upg84">
        <CheckCircle2 className={`w-10 h-10 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-992" data-mp="1nphdmqkhvinu" />
      </motion.div>
      <h3 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-993" data-mp="1iotf7jtxrzjl">
        Connected
      </h3>
      <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-994" data-mp="1eqdrfdq16n54">
        Your calendar is synced with Vexu.ai
      </p>
    </motion.div>;
  // ─── Error ───────────────────────────────────────────────
  const renderError = () => <motion.div key="error" {...fadeSlide} className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-id="element-995" data-mp="1unesrdqbimp4">
      <div className={`w-20 h-20 rounded-full border flex items-center justify-center mb-6 transition-colors ${isLight ? 'bg-red-50 border-red-200' : 'bg-red-500/10 border-red-500/20'}`} data-id="element-996" data-mp="1ym45d43j807p">
        <AlertCircle className={`w-10 h-10 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-997" data-mp="1plfq662ejtt8" />
      </div>
      <h3 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-998" data-mp="1fpclotk2vj34">
        Connection Failed
      </h3>
      <p className={`text-sm mb-6 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-999" data-mp="3633g467hhnqg">
        We couldn't connect to your calendar. Please try again.
      </p>
      <Button variant="secondary" onClick={handleRetry} icon={<RefreshCw className="w-4 h-4" data-id="element-1001" data-mp="ueow1191txap" />} data-id="element-1000" data-mp="yf8se2lsswvs">
        Retry Connection
      </Button>
    </motion.div>;
  // ─── Connected: Calendar ─────────────────────────────────
  const renderConnected = () => <motion.div key="connected" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="flex-1 flex flex-col h-full" data-id="element-1002" data-mp="13fd9nuuj5vn2">
      {/* Header */}
      <div className="px-6 pt-6 pb-2" data-id="element-1003" data-mp="1d5g0boe6ixi8">
        <div className="flex justify-between items-center mb-5" data-id="element-1004" data-mp="1d4eovit948zz">
          <h1 className={`text-2xl font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1005" data-mp="1r4xuyke5p4zi">
            Calendar
          </h1>
          <button onClick={() => setShowSettings(true)} className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.08]'}`} data-id="element-1006" data-mp="1m3rxk99jwtjn">
            <Settings className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1007" data-mp="1wso0hzwzotxs" />
          </button>
        </div>

        {/* View toggle */}
        <div className={`flex p-1 border rounded-xl mb-5 transition-colors ${isLight ? 'bg-slate-200/50 border-slate-200' : 'bg-white/[0.04] border-white/[0.06]'}`} data-id="element-1008" data-mp="21udf1pjf7pqm">
          {(['day', 'month'] as const).map(v => <button key={v} onClick={() => setView(v)} className={`relative flex-1 py-1.5 text-sm font-medium rounded-lg capitalize transition-all ${view === v ? isLight ? 'text-slate-900 shadow-sm' : 'text-white' : isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-1009" data-mp="2fr0ga4iep22c">
              {view === v && <motion.div layoutId="calViewToggle" className={`absolute inset-0 rounded-lg border ${isLight ? 'bg-white border-slate-200' : 'bg-white/[0.08] border-white/[0.06]'}`} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 35
          }} data-id="element-1010" data-mp="2yhi5hz415fiy" />}
              <span className="relative z-10" data-id="element-1011" data-mp="8w6s8m207vj5">{v}</span>
            </button>)}
        </div>

        {/* Date nav */}
        <div className="flex items-center justify-between mb-4" data-id="element-1012" data-mp="15kmhsi5464w9">
          <h2 className={`text-base font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1013" data-mp="11kvjzuh66oi8">
            {view === 'day' ? selectedDate === TODAY_DAY ? `Today, Feb ${selectedDate}` : `Feb ${selectedDate}, 2026` : 'February 2026'}
          </h2>
          <div className="flex gap-1.5" data-id="element-1014" data-mp="1papvo8pr670w">
            <button className={`p-1.5 rounded-lg border transition-colors ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.06] hover:bg-white/[0.08]'}`} data-id="element-1015" data-mp="1kiye2nokrbmw">
              <ChevronLeft className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1016" data-mp="xmluchlt0id6" />
            </button>
            <button className={`p-1.5 rounded-lg border transition-colors ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm' : 'bg-white/[0.04] border-white/[0.06] hover:bg-white/[0.08]'}`} data-id="element-1017" data-mp="nw0me3k8q1hl">
              <ChevronRight className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1018" data-mp="1gky15wp8tg5l" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-36" data-id="element-1019" data-mp="16l77fx8ib56k">
        <AnimatePresence mode="wait" data-id="element-1020" data-mp="3nbe25gp7qjxh">
          {!hasCalendarData ? <motion.div key="empty" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3
        }} data-id="element-1021" data-mp="3nd8omzg4uk3o">
              {view === 'day' /* Empty Day View */ ? <div className="flex flex-col items-center justify-center text-center pt-16" data-id="element-1022" data-mp="3otmdjj527b3g">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1023" data-mp="313ifrsejy5kd">
                    <CalendarDays className={`w-7 h-7 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1024" data-mp="3ass91bl4tua2" />
                  </div>
                  <p className={`text-sm font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1025" data-mp="2ibzbzjcpenha">
                    No events today
                  </p>
                  <p className={`text-xs leading-relaxed max-w-[220px] mb-5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1026" data-mp="3vk0cudqky23s">
                    Your calendar is synced but there are no events scheduled
                    for today.
                  </p>
                  <button onClick={() => setHasCalendarData(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-vexu text-white text-sm font-medium hover:bg-vexu-600 transition-colors" data-id="element-1027" data-mp="3gpq07yi340b">
                    <Zap className="w-4 h-4" data-id="element-1028" data-mp="d60u1g6j5ihc" />
                    Simulate Events
                  </button>
                </div> /* Empty Month View — show grid but no event dots */ : <div data-id="element-1029" data-mp="n2cbekkz8yin">
                  <div className={`backdrop-blur-xl border rounded-2xl p-4 ${isLight ? 'bg-white/80 border-slate-200/60 shadow-sm' : 'bg-white/[0.03] border-white/[0.07]'}`} data-id="element-1030" data-mp="2sxf3bunz1fxm">
                    <div className="grid grid-cols-7 gap-0.5 mb-1" data-id="element-1031" data-mp="2erd36uvmoi9l">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className={`text-center text-[11px] font-bold py-2 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1032" data-mp="2isov571z5fky" data-mpi={i}>
                          {d}
                        </div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5" data-id="element-1033" data-mp="1v68m825yw8a9">
                      {Array.from({
                  length: 35
                }).map((_, i) => {
                  const day = i + 1;
                  const inMonth = day >= 1 && day <= 28;
                  const isToday = day === TODAY_DAY;
                  const isSelected = day === selectedDate && inMonth;
                  return <button key={i} onClick={() => inMonth && setSelectedDate(day)} disabled={!inMonth} className={`
                              aspect-square flex flex-col items-center justify-center rounded-xl relative cursor-pointer transition-all
                              ${isSelected ? 'bg-vexu text-white shadow-[0_4px_12px_rgba(0,200,83,0.3)] scale-[1.02]' : isToday ? isLight ? 'bg-vexu/10 border border-vexu/20' : 'bg-vexu/15 border border-vexu/25' : isLight ? 'hover:bg-slate-100 active:bg-slate-200' : 'hover:bg-white/[0.04] active:bg-white/[0.08]'}
                              ${!inMonth ? 'opacity-20 cursor-default' : ''}
                            `} data-id="element-1034" data-mp="3lfa5ttgbk7sp" data-mpi={i}>
                            <span className={`text-[13px] ${isSelected ? 'text-white font-bold' : isToday ? isLight ? 'text-vexu-700 font-bold' : 'text-vexu font-bold' : isLight ? 'text-slate-700 font-medium' : 'text-zinc-300 font-medium'}`} data-id="element-1035" data-mp="3o0hpiiww98xb">
                              {inMonth ? day : ''}
                            </span>
                            {isToday && !isSelected && <div className={`w-1 h-1 rounded-full absolute bottom-1.5 ${isLight ? 'bg-vexu-600' : 'bg-vexu'}`} data-id="element-1036" data-mp="11stx2d7ms7ew" />}
                          </button>;
                })}
                    </div>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center pt-8" data-id="element-1037" data-mp="1frbexuauupow">
                    <p className={`text-sm font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1038" data-mp="1kkmq27mp9smm">
                      No events this month
                    </p>
                    <p className={`text-xs leading-relaxed max-w-[220px] mb-5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1039" data-mp="1ug7o5uone8r3">
                      Events booked by Vexu.ai or from your calendar will appear
                      here.
                    </p>
                    <button onClick={() => setHasCalendarData(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-vexu text-white text-sm font-medium hover:bg-vexu-600 transition-colors" data-id="element-1040" data-mp="350sydqf60387">
                      <Zap className="w-4 h-4" data-id="element-1041" data-mp="2d9wkqnla5ujv" />
                      Simulate Events
                    </button>
                  </div>
                </div>}
            </motion.div> : view === 'day' ? <motion.div key="dayView" initial={{
          opacity: 0,
          x: -12
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 12
        }} transition={{
          duration: 0.25
        }} className="space-y-3" data-id="element-1042" data-mp="2hb83qa9nfhj3">
              {EVENTS.map((event, i) => <motion.button key={event.id} onClick={() => setSelectedEvent(event)} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.06,
            duration: 0.3
          }} className={`w-full text-left backdrop-blur-xl border rounded-2xl p-4 relative overflow-hidden group transition-all active:scale-[0.99] ${isLight ? 'bg-white/80 border-slate-200/60 shadow-sm hover:bg-white hover:shadow-md' : 'bg-white/[0.03] border-white/[0.07] shadow-xl hover:bg-white/[0.05]'}`} data-id="element-1043" data-mp="3r97wgdovu9qw" data-mpi={i}>
                  {event.aiManaged && <div className={`absolute top-0 right-0 text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-bl-xl border-b border-l ${isLight ? 'bg-vexu/10 text-vexu-700 border-vexu/20' : 'bg-vexu/10 text-vexu border-vexu/15'}`} data-id="element-1044" data-mp="1ld0uo2hndz86">
                      AI Scheduled
                    </div>}
                  <div className="flex items-start gap-3.5" data-id="element-1045" data-mp="12fpzasz4zpy0">
                    <div className={`w-1 h-14 rounded-full mt-0.5 ${event.accent} opacity-80`} data-id="element-1046" data-mp="12haw6p8xbkz1" />
                    <div className="flex-1 min-w-0" data-id="element-1047" data-mp="sove7rxmmwe5">
                      <h3 className={`font-semibold text-[15px] mb-1.5 truncate pr-16 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1048" data-mp="spoeca22ynnb">
                        {event.title}
                      </h3>
                      <div className={`flex items-center gap-1.5 text-xs mb-3 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1049" data-mp="ep3tl8cluhr6">
                        <Clock className="w-3.5 h-3.5 shrink-0" data-id="element-1050" data-mp="2vc1z8clq1hyr" />
                        <span data-id="element-1051" data-mp="2va8skxb3yxqr">{event.time}</span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1052" data-mp="2zeq82kafqovq">
                        <div className={`flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-lg border truncate ${isLight ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-white/[0.04] border-white/[0.06] text-zinc-300'}`} data-id="element-1053" data-mp="34ci0dfezsd0g">
                          {event.type === 'video' ? <Video className="w-3 h-3 shrink-0" data-id="element-1054" data-mp="2glmh56olqq4t" /> : <Phone className="w-3 h-3 shrink-0" data-id="element-1055" data-mp="2lfgu7r9pc6ux" />}
                          <span className="truncate" data-id="element-1056" data-mp="1nxyy3w2zc7ka">{event.location}</span>
                        </div>
                        <div className="flex -space-x-1.5" data-id="element-1057" data-mp="1sw0h6h0eo6vr">
                          {event.attendees.slice(0, 3).map((att, j) => <div key={j} className={`w-6 h-6 rounded-full border flex items-center justify-center text-[9px] font-bold ${isLight ? 'bg-slate-100 border-white text-slate-600' : 'bg-white/[0.06] border-dark text-zinc-400'}`} data-id="element-1058" data-mp="hb60qdeve6s9" data-mpi={j}>
                              {att.charAt(0)}
                            </div>)}
                          {event.attendees.length > 3 && <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-[9px] font-bold ${isLight ? 'bg-slate-100 border-white text-slate-500' : 'bg-white/[0.06] border-dark text-zinc-500'}`} data-id="element-1059" data-mp="1eyya05r7cyg7">
                              +{event.attendees.length - 3}
                            </div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.button>)}
            </motion.div> : <motion.div key="monthView" initial={{
          opacity: 0,
          x: 12
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -12
        }} transition={{
          duration: 0.25
        }} data-id="element-1060" data-mp="1tln7k21w91jy">
              <div className={`backdrop-blur-xl border rounded-2xl p-4 ${isLight ? 'bg-white/80 border-slate-200/60 shadow-sm' : 'bg-white/[0.03] border-white/[0.07]'}`} data-id="element-1061" data-mp="19y5yw4gob2ud">
                <div className="grid grid-cols-7 gap-0.5 mb-1" data-id="element-1062" data-mp="10wpovxtrt2qo">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className={`text-center text-[11px] font-bold py-2 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1063" data-mp="15v9cs3rxcolo" data-mpi={i}>
                      {d}
                    </div>)}
                </div>
                <div className="grid grid-cols-7 gap-0.5" data-id="element-1064" data-mp="1t4tdwclxiya">
                  {Array.from({
                length: 35
              }).map((_, i) => {
                const day = i + 1;
                const inMonth = day >= 1 && day <= 28;
                const isToday = day === TODAY_DAY;
                const isSelected = day === selectedDate && inMonth;
                const hasEvent = EVENT_DAYS.includes(day);
                return <button key={i} onClick={() => inMonth && setSelectedDate(day)} disabled={!inMonth} className={`
                          aspect-square flex flex-col items-center justify-center rounded-xl relative cursor-pointer transition-all
                          ${isSelected ? 'bg-vexu text-white shadow-[0_4px_12px_rgba(0,200,83,0.3)] scale-[1.02]' : isToday ? isLight ? 'bg-vexu/10 border border-vexu/20' : 'bg-vexu/15 border border-vexu/25' : isLight ? 'hover:bg-slate-100 active:bg-slate-200' : 'hover:bg-white/[0.04] active:bg-white/[0.08]'}
                          ${!inMonth ? 'opacity-20 cursor-default' : ''}
                        `} data-id="element-1065" data-mp="pj8vu0e27cnp" data-mpi={i}>
                        <span className={`text-[13px] ${isSelected ? 'text-white font-bold' : isToday ? isLight ? 'text-vexu-700 font-bold' : 'text-vexu font-bold' : isLight ? 'text-slate-700 font-medium' : 'text-zinc-300 font-medium'}`} data-id="element-1066" data-mp="2xrnfvslcyocp">
                          {inMonth ? day : ''}
                        </span>
                        {hasEvent && inMonth && <div className={`w-1 h-1 rounded-full absolute bottom-1.5 ${isSelected ? 'bg-white' : isToday ? isLight ? 'bg-vexu-600' : 'bg-vexu' : isLight ? 'bg-slate-400' : 'bg-zinc-500'}`} data-id="element-1067" data-mp="3gpzqlirx46ae" />}
                        {isToday && !isSelected && !hasEvent && <div className={`w-1 h-1 rounded-full absolute bottom-1.5 ${isLight ? 'bg-vexu-600' : 'bg-vexu'}`} data-id="element-1068" data-mp="1a2itr4tco030" />}
                      </button>;
              })}
                </div>
              </div>

              <div className="mt-5" data-id="element-1069" data-mp="m53edvaq2dxv">
                <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1070" data-mp="m64sy9qy5ikc">
                  Events on Feb {selectedDate}
                </h3>
                <div className="space-y-2.5" data-id="element-1071" data-mp="2lho14m4femsy">
                  {EVENTS.slice(0, 2).map(event => <button key={event.id} onClick={() => setSelectedEvent(event)} className={`w-full text-left border rounded-xl p-3 flex items-center gap-3 transition-all active:scale-[0.99] ${isLight ? 'bg-white border-slate-200 shadow-sm hover:bg-slate-50' : 'bg-white/[0.03] border-white/[0.07] hover:bg-white/[0.05]'}`} data-id="element-1072" data-mp="9uc7jnzeiwsl">
                      <div className={`w-1 h-10 rounded-full ${event.accent} opacity-80`} data-id="element-1073" data-mp="skansrdcrw7b" />
                      <div className="flex-1 min-w-0" data-id="element-1074" data-mp="nl7id5tgnnyt">
                        <h4 className={`text-sm font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1075" data-mp="12iq7i9a1w36s">
                          {event.title}
                        </h4>
                        <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1076" data-mp="34b60kyhyhzf2">
                          {event.time}
                        </p>
                      </div>
                      {event.aiManaged && <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md border ${isLight ? 'text-vexu-700 bg-vexu/10 border-vexu/20' : 'text-vexu bg-vexu/10 border-vexu/15'}`} data-id="element-1077" data-mp="1vcd2kfp47trn">
                          AI
                        </span>}
                    </button>)}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
  return <div className="h-full flex flex-col relative overflow-hidden" data-id="element-1078" data-mp="1gmywphkqzuh0">
      {/* Background orbs */}
      <div className={`absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/10' : 'bg-vexu/[0.04]'}`} data-id="element-1079" data-mp="1lgta639rfd7n" />
      <div className={`absolute bottom-32 -left-20 w-64 h-64 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-blue-500/10' : 'bg-blue-500/[0.03]'}`} data-id="element-1080" data-mp="syfkiqykuxjn" />

      <AnimatePresence mode="wait" data-id="element-1081" data-mp="ggbvu5rbbsf8">
        {connectionState === 'disconnected' && renderDisconnected()}
        {connectionState === 'connecting' && renderConnecting()}
        {connectionState === 'success' && renderSuccess()}
        {connectionState === 'error' && renderError()}
        {connectionState === 'connected' && renderConnected()}
      </AnimatePresence>

      {/* Reminder Toast */}
      <AnimatePresence data-id="element-1082" data-mp="3tv897hrh03t6">
        {reminderToast && <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        type: 'spring',
        stiffness: 350,
        damping: 28
      }} className="absolute bottom-28 left-4 right-4 z-[70] flex justify-center" data-id="element-1083" data-mp="3ttoqlkicdgo8">
            <div className={`flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl max-w-[320px] ${isLight ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'}`} data-id="element-1084" data-mp="3684jq39b7ncy">
              <Bell className="w-4 h-4 text-vexu flex-shrink-0" data-id="element-1085" data-mp="2w8vn12n27umw" />
              <span className="text-xs font-medium leading-snug" data-id="element-1086" data-mp="3jz9diopzxlfd">
                {reminderToast}
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Event Detail Sheet */}
      <AnimatePresence data-id="element-1087" data-mp="3aa8096au5i53">
        {selectedEvent && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0 z-50 flex items-end justify-center" data-id="element-1088" data-mp="3a7df1cjzztam">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedEvent(null)} data-id="element-1089" data-mp="1w8jy0enetk9e" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 32
        }} className={`relative w-full max-h-[88%] rounded-t-3xl overflow-hidden flex flex-col ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border-t border-white/10'}`} data-id="element-1090" data-mp="25zemzqs1ixyj">
              {/* Drag handle */}
              <div className="pt-2 pb-1 flex justify-center flex-shrink-0" data-id="element-1091" data-mp="2xqu8fhhv2en6">
                <div className={`w-10 h-1 rounded-full ${isLight ? 'bg-slate-200' : 'bg-white/15'}`} data-id="element-1092" data-mp="2xsx7ss4wlk4k" />
              </div>

              {/* Accent header strip */}
              <div className="px-6 pt-3 pb-5 flex-shrink-0" data-id="element-1093" data-mp="r0h4oppgw9f7">
                <div className="flex items-start justify-between gap-3 mb-4" data-id="element-1094" data-mp="w2pz7qhznjjf">
                  <div className="flex items-start gap-3 min-w-0" data-id="element-1095" data-mp="1107yep67eke8">
                    <div className={`w-1.5 h-12 rounded-full flex-shrink-0 mt-0.5 ${selectedEvent.accent}`} data-id="element-1096" data-mp="10ywwyw08doqa" />
                    <div className="min-w-0" data-id="element-1097" data-mp="19s1mcwo2axdu">
                      <h2 className={`text-xl font-bold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1098" data-mp="19vflzx5ccshl">
                        {selectedEvent.title}
                      </h2>
                      <p className={`text-sm mt-1 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1099" data-mp="1oqlucqrwt3e3">
                        {selectedEvent.date}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedEvent(null)} className={`p-1.5 rounded-full flex-shrink-0 ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-1100" data-mp="27mknnnayam7a">
                    <X className="w-4 h-4" data-id="element-1101" data-mp="1yv26pa66k2d5" />
                  </button>
                </div>

                {selectedEvent.aiManaged && <div className="space-y-2.5" data-id="element-1102" data-mp="2qlgh2weziwk2">
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${isLight ? 'bg-vexu/10 border border-vexu/20' : 'bg-vexu/10 border border-vexu/20'}`} data-id="element-1103" data-mp="2qk5egzx9zuuj">
                      <Bot className={`w-3.5 h-3.5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1104" data-mp="2hixjortyxc57" />
                      <span className={`text-xs font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1105" data-mp="1664spj904rq7">
                        Scheduled by your AI agent
                      </span>
                    </div>
                    {selectedEvent.originatingCall && <button type="button" onClick={() => {
                onViewCall?.(selectedEvent.originatingCall!.id);
                setSelectedEvent(null);
              }} className={`flex w-full items-center justify-between rounded-xl border px-3.5 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300' : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20'}`} data-id="element-1106" data-mp="1yk2r9vxlvkor">
                        <span className="flex items-center gap-2.5 min-w-0" data-id="element-1107" data-mp="15sj2lwi8pefd"><span className={`w-8 h-8 rounded-lg grid place-items-center flex-shrink-0 ${isLight ? 'bg-white text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-1108" data-mp="10u7rzjk4ezw3"><PhoneCall className="w-4 h-4" data-id="element-1109" data-mp="wvs9nsyzifp0" /></span><span className="min-w-0" data-id="element-1110" data-mp="38hkcg6kq8c9c"><span className={`block text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1111" data-mp="38fzleqxmfbg8">Originating call</span><span className={`mt-0.5 block truncate text-xs font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-1112" data-mp="3ckh0ict1d0ko">{selectedEvent.originatingCall.label}</span></span></span>
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1113" data-mp="3mepp4pey49dk" />
                      </button>}
                  </div>}
              </div>

              <div className={`flex-1 overflow-y-auto px-6 pb-6 space-y-5 border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-1114" data-mp="2yqprd5j7viph">
                {/* Time */}
                <div className="flex items-start gap-3.5 pt-5" data-id="element-1115" data-mp="263293x5f5460">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1116" data-mp="260fzzt2vqglo">
                    <Clock className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1117" data-mp="22z4meisv0vvu" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1118" data-mp="2cppf5plur66u">
                    <p className={`text-[11px] font-medium uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1119" data-mp="2cslcbbpvlqu8">
                      Time
                    </p>
                    <p className={`text-sm font-semibold mt-0.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1120" data-mp="1g0zw3vyho7ja">
                      {selectedEvent.time}
                    </p>
                    <p className={`text-xs mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1121" data-mp="1p04tgyybdutv">
                      {selectedEvent.duration}
                    </p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-3.5" data-id="element-1122" data-mp="m7ivlql612tx">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1123" data-mp="r7ncvitwe1l7">
                    {selectedEvent.type === 'video' ? <Video className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1124" data-mp="i80dt3on57vb" /> : <Phone className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1125" data-mp="19yob24n71t3p" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1126" data-mp="115l2a9nhu4fe">
                    <p className={`text-[11px] font-medium uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1127" data-mp="1149xq7m84sn8">
                      {selectedEvent.type === 'video' ? 'Video call' : 'Phone call'}
                    </p>
                    <p className={`text-sm font-semibold mt-0.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1128" data-mp="17zenmi31f7">
                      {selectedEvent.location}
                    </p>
                    {selectedEvent.meetingLink && <a href="#" onClick={e => e.preventDefault()} className={`inline-flex items-center gap-1 text-xs font-medium mt-1 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1129" data-mp="3n80nqb9uexes">
                        <Link2 className="w-3 h-3" data-id="element-1130" data-mp="ewxr92eda8ru" />
                        {selectedEvent.meetingLink}
                      </a>}
                  </div>
                </div>

                {/* Organizer */}
                <div className="flex items-start gap-3.5" data-id="element-1131" data-mp="17gmkoeizvw6y">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1132" data-mp="12nbac3ez4xaa">
                    <MapPin className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1133" data-mp="1ezdkyajqpm6o" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1134" data-mp="1nyqypfewcwx7">
                    <p className={`text-[11px] font-medium uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1135" data-mp="1o0k5cupifh57">
                      Organizer
                    </p>
                    <p className={`text-sm font-semibold mt-0.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1136" data-mp="1xuu2epped3m9">
                      {selectedEvent.organizer}
                    </p>
                  </div>
                </div>

                {/* Attendees */}
                <div className="flex items-start gap-3.5" data-id="element-1137" data-mp="2gno6golhfbbp">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1138" data-mp="2go7khu80hfkg">
                    <Users className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1139" data-mp="cl4mld7mvy8m" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1140" data-mp="ljzuquu8niek">
                    <p className={`text-[11px] font-medium uppercase tracking-wider mb-2 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1141" data-mp="lj6veexlzv6g">
                      {selectedEvent.attendees.length} Attendees
                    </p>
                    <div className="space-y-2" data-id="element-1142" data-mp="199190ypr5nrr">
                      {selectedEvent.attendees.map((att, idx) => <div key={idx} className="flex items-center gap-2.5" data-id="element-1143" data-mp="14bivev0sikdf" data-mpi={idx}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-1144" data-mp="3b2ib7mv45wf4">
                            {att.charAt(0)}
                          </div>
                          <span className={`text-sm ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1145" data-mp="1qin2yk62pfmy">
                            {att}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="flex items-start gap-3.5" data-id="element-1146" data-mp="17ui1xkgsw8ru">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1147" data-mp="ttnouoit5jmu">
                    <FileText className={`w-4 h-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1148" data-mp="2o7gb7im5d3c9" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1149" data-mp="2s6o1zklzbfr1">
                    <p className={`text-[11px] font-medium uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1150" data-mp="1fzok80y6edar">
                      Notes
                    </p>
                    <p className={`text-sm leading-relaxed mt-1 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-1151" data-mp="16810ynbrw2kd">
                      {selectedEvent.description}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2" data-id="element-1152" data-mp="s8j73tjmdz45">
                  <button onClick={() => toggleReminder(selectedEvent)} className={`flex-1 py-3 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2 ${reminderIds.includes(selectedEvent.id) ? isLight ? 'bg-vexu/10 text-vexu-700 border border-vexu/20' : 'bg-vexu/15 text-vexu border border-vexu/20' : isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-1153" data-mp="na7whgli3kkv">
                    {reminderIds.includes(selectedEvent.id) ? <>
                        <CheckCircle2 className="w-4 h-4" data-id="element-1154" data-mp="2tyi2112l8qd1" />
                        Reminder set
                      </> : <>
                        <Bell className="w-4 h-4" data-id="element-1155" data-mp="227u0hnx05iyu" />
                        Remind me
                      </>}
                  </button>
                  {selectedEvent.type === 'video' && selectedEvent.meetingLink ? <button className="flex-1 py-3 rounded-xl font-medium text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors flex items-center justify-center gap-2" data-id="element-1156" data-mp="2b6xkqv7wo4ex">
                      <Video className="w-4 h-4" data-id="element-1157" data-mp="3mmm83np2n5g9" />
                      Join
                    </button> : selectedEvent.type === 'phone' ? <button className="flex-1 py-3 rounded-xl font-medium text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors flex items-center justify-center gap-2" data-id="element-1158" data-mp="1wx9lyiya1b77">
                      <Phone className="w-4 h-4" data-id="element-1159" data-mp="210z7ezjnnco0" />
                      Call
                    </button> : null}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Calendar Settings Sheet */}
      <AnimatePresence data-id="element-1160" data-mp="1d7z8zcpx14cs">
        {showSettings && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0 z-50 flex items-end justify-center" data-id="element-1161" data-mp="1i8n9cp01axjo">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowSettings(false)} data-id="element-1162" data-mp="33lpzwfe44qvd" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 32
        }} className={`relative w-full max-h-[85%] rounded-t-3xl overflow-hidden flex flex-col ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border-t border-white/10'}`} data-id="element-1163" data-mp="3dq6fr8rle7z6">
              {/* Drag handle */}
              <div className="pt-2 pb-1 flex justify-center" data-id="element-1164" data-mp="9aezcynclhva">
                <div className={`w-10 h-1 rounded-full ${isLight ? 'bg-slate-200' : 'bg-white/15'}`} data-id="element-1165" data-mp="5a4lz0x87p6e" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-3 pb-4 border-b ${isLight ? 'border-slate-100' : 'border-white/5'}" data-id="element-1166" data-mp="2717xdsexx7s0">
                <div data-id="element-1167" data-mp="a1nao88h8qvh">
                  <h2 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1168" data-mp="a253euvxkmyn">
                    Calendar accounts
                  </h2>
                  <p className={`text-xs mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1169" data-mp="jx6u3v5utx3c">
                    Manage your connected calendars
                  </p>
                </div>
                <button onClick={() => setShowSettings(false)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/5'}`} data-id="element-1170" data-mp="xyj8411gfoml">
                  <X className="w-4 h-4" data-id="element-1171" data-mp="17om0q04nz0ke" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-1172" data-mp="1cnytf1i7v1uw">
                {/* Connected accounts */}
                {connectedAccounts.length > 0 && <div className="mb-6" data-id="element-1173" data-mp="1gmxto8t06ag0">
                    <p className={`text-[11px] font-bold uppercase tracking-wider mb-2.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1174" data-mp="317bexjsxdk2s">
                      Connected ({connectedAccounts.length})
                    </p>
                    <div className="space-y-2" data-id="element-1175" data-mp="2n7a359b4gs8j">
                      {connectedAccounts.map(acc => <div key={acc.id} className={`flex items-center gap-3 p-3 rounded-2xl border ${isLight ? 'bg-white border-slate-200' : 'bg-white/[0.03] border-white/[0.08]'}`} data-id="element-1176" data-mp="3kv2agw3w99tu">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-vexu/10' : 'bg-vexu/15'}`} data-id="element-1177" data-mp="3b3ehgqrovkq9">
                            {acc.provider === 'phone' ? <Smartphone className={`w-5 h-5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1178" data-mp="2oe8vg81z4sx" /> : acc.provider === 'icloud' ? <Cloud className={`w-5 h-5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1179" data-mp="3nfq9yqamtuax" /> : <Mail className={`w-5 h-5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1180" data-mp="3efti6fsy4fbn" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1181" data-mp="3sdi2nmj3pkg6">
                            <div className="flex items-center gap-1.5" data-id="element-1182" data-mp="3scyuuyn60cfv">
                              <p className={`text-sm font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1183" data-mp="165kb46hf0yo">
                                {acc.label}
                              </p>
                              <CheckCircle2 className="w-3.5 h-3.5 text-vexu flex-shrink-0" data-id="element-1184" data-mp="2edx3afhxfa6m" />
                            </div>
                            <p className={`text-xs truncate mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1185" data-mp="29flso2jt4vnc">
                              {acc.detail}
                            </p>
                          </div>
                          <button onClick={() => handleRemoveAccount(acc.id)} className={`p-2 rounded-full flex-shrink-0 ${isLight ? 'text-red-500 hover:bg-red-50' : 'text-red-400 hover:bg-red-500/10'}`} data-id="element-1186" data-mp="1vermzro598sd">
                            <Trash2 className="w-4 h-4" data-id="element-1187" data-mp="1zgmmqrqffe41" />
                          </button>
                        </div>)}
                    </div>
                  </div>}

                {/* Add another account */}
                <div data-id="element-1188" data-mp="3av9tl07t50g9">
                  <p className={`text-[11px] font-bold uppercase tracking-wider mb-2.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1189" data-mp="3axw51axtk03r">
                    {connectedAccounts.length > 0 ? 'Add another' : 'Available providers'}
                  </p>
                  <div className="space-y-2" data-id="element-1190" data-mp="2miodt4dms1a3">
                    {ALL_PROVIDERS.filter(p => !connectedAccounts.some(a => a.provider === p.id)).map(p => {
                  const isAdding = addingProvider === p.id;
                  return <button key={p.id} onClick={() => !isAdding && handleAddAccount(p.id)} disabled={isAdding} className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition-colors ${isLight ? 'bg-white border-slate-200 hover:bg-slate-50 active:bg-slate-100' : 'bg-white/[0.03] border-white/[0.08] hover:bg-white/[0.06]'}`} data-id="element-1191" data-mp="1yrsgxsubifvz">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-1192" data-mp="yk41w3oklb89">
                            {p.id === 'phone' ? <Smartphone className={`w-5 h-5 ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1193" data-mp="kkmcpn7d3vyd" /> : p.id === 'icloud' ? <Cloud className={`w-5 h-5 ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1194" data-mp="au1hm9qwd9k7" /> : <Mail className={`w-5 h-5 ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1195" data-mp="1t38xe5ikdva" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1196" data-mp="1ksj17mdb87m7">
                            <p className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1197" data-mp="1pr2p3sbgrth7">
                              {p.label}
                            </p>
                            <p className={`text-xs mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1198" data-mp="1fqtrqb5qk208">
                              {isAdding ? 'Connecting...' : p.detail}
                            </p>
                          </div>
                          {isAdding ? <Loader2 className={`w-4 h-4 animate-spin flex-shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1199" data-mp="x1lzt3j54sp7" /> : <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-1200" data-mp="o0fi4r4r9bud">
                              <Plus className="w-4 h-4" data-id="element-1201" data-mp="3vo2xn4hnqti2" />
                            </div>}
                        </button>;
                })}
                    {ALL_PROVIDERS.every(p => connectedAccounts.some(a => a.provider === p.id)) && <div className={`text-center text-xs py-6 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1202" data-mp="ig877adms3e5">
                        All available calendar providers are connected.
                      </div>}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}